import { formatMoney, formatRelative } from "@micropod/i18n";
import { useNow } from "./clock";

/**
 * Formatted values.
 *
 * These exist so no screen ever hand-rolls one. A phone number masked three different ways
 * in three files is three chances to leak one, and money divided by 100 in a component is
 * how a zero-decimal currency gets silently wrong.
 */

/**
 * Masked by default — this screen sits in an open-plan office and gets screen-shared.
 * Stored as E.164; the owner recognises it, the room does not.
 */
export function PhoneNumber({ value, reveal = false }: { value: string; reveal?: boolean }) {
  const e164 = value.startsWith("+") ? value : `+${value}`;
  if (reveal) return <span className="mono">{e164}</span>;
  const masked =
    e164.length < 7 ? "•".repeat(e164.length) : `${e164.slice(0, 3)} ••••• ••${e164.slice(-3)}`;
  return (
    <span className="mono" title="Masked — the full number is on the contact record">
      {masked}
    </span>
  );
}

/**
 * The only place currency is decided.
 *
 * Integer minor units plus an ISO 4217 code in; a locale-correct string out. An amount
 * never travels without its currency, and zero-decimal currencies are not divided by 100.
 */
export function MoneyAmount({
  minor,
  currency,
  locale,
}: {
  minor: number;
  currency: string;
  locale: string;
}) {
  return <span className="tabular">{formatMoney(minor, currency, locale)}</span>;
}

const MINUTE = 60_000;

/** Relative time, driven by the one shared clock rather than a timer of its own. */
export function RelativeTime({ at, locale }: { at: number; locale: string }) {
  // "30s ago" changes every second and "2h ago" changes once an hour, so only the first
  // minute is worth a per-second subscription. docs/08 §3 P3.
  const coarse = useNow("minute");
  const now = useNow(coarse - at < MINUTE ? "second" : "minute");
  return (
    <time dateTime={new Date(at).toISOString()} className="reltime tabular">
      {/* The clock reads DOWN to its resolution, so it can sit a moment behind a timestamp
          that has only just arrived. Without the clamp a message sent one second ago reads
          "in 59s" until the next minute boundary. */}
      {formatRelative(at, Math.max(now, at), locale)}
    </time>
  );
}
